import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Archive } from "lucide-react";

import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { EmptyDataPlaceholder } from "@/components/src/empty-with-label/empty-data-placeholder.component";
import { FileViewerModal } from "@/components/src/file-viewer/file-viewer-modal/file-viewer-modal.component";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { ArchiveFileType } from "@/lib/supabase/supabaseTypes";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "../../store/Central/selectors";

import { useCentralStore } from "../../store/Central";
import { useArchiveStore } from "../../store/Archive";
import {
	groupKey,
	isClientScope,
	isFlatView,
	NO_CLIENT_SCOPE,
} from "../../store/Archive/archive.store";
import { FeatureGateOverlay } from "../../components/subscription/feature-gate-overlay.component";
import { useStorage } from "../../hooks/useStorage";
import { cn } from "@lab/src/lib/util";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { SearchInput } from "../../components/ui/search-input.component";
import { ArchiveClientCombobox } from "./components/archive-client-combobox.component";
import { ArchiveFacetSidebar } from "./components/archive-facet-sidebar.component";
import { ArchiveControls } from "./components/archive-controls.component";
import { ArchiveJobGroupRow } from "./components/archive-job-group.component";
import { ArchiveFlatFileList } from "./components/archive-flat-file-list.component";
import { ArchiveSelectionToolbar } from "./components/archive-selection-toolbar.component";
import { useArchiveZip } from "./use-archive-zip";
import { formatArchiveDateRange } from "./archive.utils";

/**
 * Archive of every file of the organisation, narrowed to one Auftraggeber or to the files
 * without one. Newest first by default; a single client can also be grouped by Auftrag and has
 * facet counts. Standalone under /archive the scope lives in ?clientId; inside the client view
 * (/client/:clientId/dateiarchiv) it is fixed to that client and the client header replaces the
 * page title and picker. Everything is filtered, sorted and paged server side.
 */
export const ArchivePage: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const { fetchFileFromPath, downloadBlob } = useStorage();
	const { isZipping, progress, downloadZip } = useArchiveZip();

	const { clientId: routeClientId } = useParams();
	const isInClientView = !!routeClientId;
	const scope = routeClientId ?? searchParams.get("clientId");
	const [preview, setPreview] = useState<{
		ids: string[];
		defaultId: string;
	} | null>(null);

	const clientsLookup = useCentralStore((state) => state.clientsLookup);
	const overview = useArchiveStore((state) => state.overview);
	const overviewFailed = useArchiveStore((state) => state.overviewFailed);
	const filteredCount = useArchiveStore((state) => state.filteredCount);
	const filterYear = useArchiveStore((state) => state.filterYear);
	const filterKinds = useArchiveStore((state) => state.filterKinds);
	const search = useArchiveStore((state) => state.search);
	const sort = useArchiveStore((state) => state.sort);
	const viewMode = useArchiveStore((state) => state.viewMode);

	const jobGroups = useArchiveStore((state) => state.jobGroups);
	const jobGroupsLoading = useArchiveStore((state) => state.jobGroupsLoading);
	const revalidating = useArchiveStore((state) => state.revalidating);
	const jobGroupsLoadingMore = useArchiveStore(
		(state) => state.jobGroupsLoadingMore
	);
	const jobGroupsHasMore = useArchiveStore((state) => state.jobGroupsHasMore);
	const expandedKeys = useArchiveStore((state) => state.expandedKeys);
	const expandedFiles = useArchiveStore((state) => state.expandedFiles);
	const expandedLoading = useArchiveStore((state) => state.expandedLoading);
	const expandedHasMore = useArchiveStore((state) => state.expandedHasMore);

	const flatFiles = useArchiveStore((state) => state.flatFiles);
	const flatLoading = useArchiveStore((state) => state.flatLoading);
	const flatLoadingMore = useArchiveStore((state) => state.flatLoadingMore);
	const flatHasMore = useArchiveStore((state) => state.flatHasMore);

	const selectedFileIds = useArchiveStore((state) => state.selectedFileIds);
	const selectAllCapReached = useArchiveStore(
		(state) => state.selectAllCapReached
	);
	const failed = useArchiveStore((state) => state.failed);

	const setFilterYear = useArchiveStore((state) => state.setFilterYear);
	const toggleFilterKind = useArchiveStore((state) => state.toggleFilterKind);
	const setSearch = useArchiveStore((state) => state.setSearch);
	const setSort = useArchiveStore((state) => state.setSort);
	const setViewMode = useArchiveStore((state) => state.setViewMode);
	const resetFilters = useArchiveStore((state) => state.resetFilters);
	const loadMoreJobGroups = useArchiveStore(
		(state) => state.loadMoreJobGroups
	);
	const loadMoreGroupFiles = useArchiveStore(
		(state) => state.loadMoreGroupFiles
	);
	const loadMoreFlatFiles = useArchiveStore(
		(state) => state.loadMoreFlatFiles
	);
	const toggleJobExpanded = useArchiveStore(
		(state) => state.toggleJobExpanded
	);
	const toggleFileSelected = useArchiveStore(
		(state) => state.toggleFileSelected
	);
	const selectFiles = useArchiveStore((state) => state.selectFiles);
	const selectAllFiltered = useArchiveStore(
		(state) => state.selectAllFiltered
	);
	const clearSelection = useArchiveStore((state) => state.clearSelection);

	const clientScope = isClientScope(scope);
	const client = clientScope ? clientsLookup[scope] : undefined;
	const selectedIdSet = useMemo(
		() => new Set(selectedFileIds),
		[selectedFileIds]
	);
	const dateRange = overview
		? formatArchiveDateRange(overview.minDate, overview.maxDate)
		: "";
	const flat = isFlatView({ scope, search, viewMode });
	const totalForSelectAll = filteredCount ?? overview?.total ?? null;
	const hasActiveFilter =
		filterYear !== null || filterKinds.length > 0 || search.trim() !== "";

	const handleSelectScope = (nextScope: string | null) => {
		setSearchParams(nextScope ? { clientId: nextScope } : {});
	};

	const handleOpenGroupJob = (jobId: number) => {
		navigate(`/client/${scope}/job/${jobId}`);
	};

	const handleOpenFileJob = (file: ArchiveFileType) => {
		navigate(
			`/client/${file.resolved_client_id}/job/${file.resolved_job_id}`
		);
	};

	const handlePreview = (file: ArchiveFileType, siblingIds: string[]) => {
		if (file.id) setPreview({ ids: siblingIds, defaultId: file.id });
	};

	const handleDownloadFile = async (file: ArchiveFileType) => {
		if (!file.path_name) return;
		const { success, data: signedUrl } = await fetchFileFromPath(
			file.path_name
		);
		if (!success || !signedUrl) {
			showNotification({
				message: "Fehler beim Herunterladen der Datei",
				type: "error",
			});
			return;
		}
		try {
			const response = await fetch(signedUrl);
			if (!response.ok) throw new Error("Download failed");
			await downloadBlob(
				await response.blob(),
				file.file_name || "Unbenannte_Datei"
			);
		} catch (error) {
			Logger.error(error, {}, "Error downloading archive file");
			showNotification({
				message: "Fehler beim Herunterladen der Datei",
				type: "error",
			});
		}
	};

	const handleDownloadZip = () => {
		let zipName = "Archiv.zip";
		if (client) zipName = `Archiv_${getClientDisplayName(client)}.zip`;
		else if (scope === NO_CLIENT_SCOPE)
			zipName = "Archiv_Ohne_Auftraggeber.zip";
		void downloadZip(selectedFileIds, zipName);
	};

	const showInitialLoading = flat ? flatLoading : jobGroupsLoading;
	const hasNoResults = flat ? flatFiles.length === 0 : jobGroups.length === 0;

	return (
		<FeatureGateOverlay>
			<div className="flex h-full w-full flex-col gap-4 p-6">
				{!isInClientView && (
					<HeaderPortal>
						<PageTitle
							title={[{ name: "Archiv" }]}
							icon={<Archive size={16} />}
						/>
					</HeaderPortal>
				)}

				<div className="flex flex-wrap items-center gap-3">
					{!isInClientView && (
						<ArchiveClientCombobox
							value={scope}
							onSelect={handleSelectScope}
							includeInactive
						/>
					)}

					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Dateien durchsuchen…"
						className="w-72"
					/>

					{overview && overview.total !== null && (
						<span
							className="text-sm text-gray-500"
							data-testid="archive-file-count"
						>
							{filteredCount !== null
								? `${filteredCount.toLocaleString(
										"de-DE"
									)} von ${overview.total.toLocaleString(
										"de-DE"
									)}`
								: overview.total.toLocaleString("de-DE")}{" "}
							{(filteredCount ?? overview.total) === 1
								? "Datei"
								: "Dateien"}
							{dateRange &&
								filteredCount === null &&
								` · ${dateRange}`}
						</span>
					)}

					{overview && overview.total === null && dateRange && (
						<span
							className="text-sm text-gray-500"
							data-testid="archive-date-range"
						>
							{dateRange}
						</span>
					)}

					{overview && (
						<ArchiveControls
							filterYear={filterYear}
							filterKinds={filterKinds}
							search={search}
							sort={sort}
							viewMode={viewMode}
							isFlat={flat}
							canGroup={clientScope}
							onReset={resetFilters}
							onSortChange={setSort}
							onViewModeChange={setViewMode}
						/>
					)}
				</div>

				{selectedFileIds.length > 0 && (
					<ArchiveSelectionToolbar
						selectedCount={selectedFileIds.length}
						totalCount={totalForSelectAll}
						capReached={selectAllCapReached}
						isZipping={isZipping}
						progress={progress}
						onSelectAll={selectAllFiltered}
						onClear={clearSelection}
						onDownloadZip={handleDownloadZip}
					/>
				)}

				{failed ? (
					<EmptyDataPlaceholder label="Archiv konnte nicht geladen werden" />
				) : (
					<div className="flex min-h-0 flex-1 gap-6">
						<ArchiveFacetSidebar
							overview={overview}
							failed={overviewFailed}
							filterYear={filterYear}
							filterKinds={filterKinds}
							onSelectYear={setFilterYear}
							onToggleKind={toggleFilterKind}
						/>

						<div className="min-w-0 flex-1 overflow-y-auto">
							{showInitialLoading ? (
								<div
									className="flex flex-col gap-2"
									aria-busy="true"
									aria-label="Wird geladen"
									data-testid="archive-loading"
								>
									{Array.from({ length: 8 }).map(
										(_, index) => (
											<Skeleton
												key={index}
												className="h-10 w-full"
											/>
										)
									)}
								</div>
							) : hasNoResults ? (
								<EmptyDataPlaceholder
									label={
										hasActiveFilter
											? "Keine Dateien passen zu den Filtern"
											: "Keine Dateien vorhanden"
									}
								/>
							) : (
								<div
									className={cn(
										"transition-opacity",
										revalidating &&
											"pointer-events-none opacity-50"
									)}
									aria-busy={revalidating}
								>
									{flat ? (
										<ArchiveFlatFileList
											files={flatFiles}
											hasMore={flatHasMore}
											loadingMore={flatLoadingMore}
											selectedIds={selectedIdSet}
											showClient={!clientScope}
											onToggleFile={toggleFileSelected}
											onPreviewFile={handlePreview}
											onDownloadFile={handleDownloadFile}
											onOpenJob={handleOpenFileJob}
											onLoadMore={loadMoreFlatFiles}
										/>
									) : (
										<>
											<div className="divide-y divide-gray-100 rounded-md border border-gray-200">
												{jobGroups.map((group) => {
													const key = groupKey(group);
													return (
														<ArchiveJobGroupRow
															key={key}
															group={group}
															isExpanded={expandedKeys.includes(
																key
															)}
															isLoading={
																!!expandedLoading[
																	key
																]
															}
															hasMore={
																!!expandedHasMore[
																	key
																]
															}
															files={
																expandedFiles[
																	key
																]
															}
															selectedIds={
																selectedIdSet
															}
															onToggleExpand={
																toggleJobExpanded
															}
															onToggleFile={
																toggleFileSelected
															}
															onSelectGroup={
																selectFiles
															}
															onPreviewFile={
																handlePreview
															}
															onDownloadFile={
																handleDownloadFile
															}
															onOpenJob={
																handleOpenGroupJob
															}
															onLoadMoreFiles={
																loadMoreGroupFiles
															}
														/>
													);
												})}
											</div>

											{jobGroupsHasMore && (
												<div className="flex justify-center py-4">
													<Button
														variant="outline"
														onClick={
															loadMoreJobGroups
														}
														disabled={
															jobGroupsLoadingMore
														}
														aria-label="Weitere Aufträge laden"
														data-testid="archive-load-more-button"
													>
														{jobGroupsLoadingMore
															? "Wird geladen…"
															: "Weitere Aufträge laden"}
													</Button>
												</div>
											)}
										</>
									)}
								</div>
							)}
						</div>
					</div>
				)}

				{preview && (
					<FileViewerModal
						fileIds={preview.ids}
						defaultFileId={preview.defaultId}
						isOpen={!!preview}
						onClose={() => setPreview(null)}
						metadata={{ clientId: clientScope ? scope : null }}
					/>
				)}
			</div>
		</FeatureGateOverlay>
	);
};
